'use client'

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { Formik, Form } from 'formik'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FormTextarea } from '@/components/form-inputs'
import { GUARDRAIL_PROMPT_MAX_LENGTH } from '@/lib/ai/guardrails'
import { AI_USE_CASES } from '@/lib/ai/langchain/types'
import { useAiUseCaseGuardrailQuery } from '@/hooks/queries/ai-use-cases'
import { useUpdateAiUseCaseGuardrailMutation } from '@/hooks/mutations/ai-use-cases'
import { useTranslations } from "next-intl"


interface GuardrailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  useCaseId: string
  useCaseSlug: string
  useCaseName: string
}

/** Editor for the platform guardrail of one use case. Text is fetched on open only. */
export function GuardrailDialog({
  open,
  onOpenChange,
  useCaseId,
  useCaseSlug,
  useCaseName,
}: GuardrailDialogProps) {
  const tUi = useTranslations("Ai.AiConfigGuardrailDialog")
  const tCommonUi = useTranslations("Common")

  const guardrailQuery = useAiUseCaseGuardrailQuery(useCaseId, open)
  const updateGuardrail = useUpdateAiUseCaseGuardrailMutation()

  const isVoice = useCaseSlug === AI_USE_CASES.VOICE_AGENT

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span><span key={tUi("platformGuardrail")}>{tUi("platformGuardrail")}</span></span> {useCaseName}</DialogTitle>
          <DialogDescription>
            <span key={tUi("insertedDirectlyUnderTheCustomerSPromptOn")}>{tUi("insertedDirectlyUnderTheCustomerSPromptOn")}</span></DialogDescription>
        </DialogHeader>

        {guardrailQuery.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : guardrailQuery.isError ? (
          // Never mount the form on a failed read: an empty field saved over a configured
          // guardrail would silently drop the platform's safety layer.
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                <span key={guardrailQuery.error instanceof Error
                  ? guardrailQuery.error.message
                  : tUi("failedToLoadTheGuardrail")}><UiErrorText message={guardrailQuery.error instanceof Error ? guardrailQuery.error.message : undefined} fallback={tUi("failedToLoadTheGuardrail")} /></span>
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <span key={tUi("close")}>{tUi("close")}</span></Button>
              <Button onClick={() => guardrailQuery.refetch()}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
            </DialogFooter>
          </div>
        ) : (
          <Formik
            initialValues={{ guardrail_prompt: guardrailQuery.data ?? '' }}
            enableReinitialize
            onSubmit={async (values, { setStatus }) => {
              try {
                await updateGuardrail.mutateAsync({
                  id: useCaseId,
                  guardrail_prompt: values.guardrail_prompt.trim(),
                })
                onOpenChange(false)
              } catch (error) {
                setStatus(error instanceof Error ? error.message : 'Failed to save guardrail')
              }
            }}
          >
            {({ isSubmitting, status }) => (
              <Form className="space-y-4">
                {status && (
                  <Alert variant="destructive">
                    <AlertDescription>{status}</AlertDescription>
                  </Alert>
                )}

                <FormTextarea
                  name="guardrail_prompt"
                  label={tUi("guardrailInstruction")}
                  placeholder={tUi("answerOnlyFromTheProvidedContextIfThe")}
                  rows={12}
                  maxLength={GUARDRAIL_PROMPT_MAX_LENGTH}
                  showLength
                  className="font-mono text-sm"
                  description={
                    isVoice
                      ? tUi("holdsTheVoiceConversationSafetyRulesAndSits")
                      : tUi("writeItAsOverridingRulesItSitsDirectly")
                  }
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <span><span key={tUi("saveGuardrail")}>{tUi("saveGuardrail")}</span></span>
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
